<template>
  <v-container>
    <div class="ml-2">
      <PageTitle text="Backlog Fuel Stock" class="font font-weight-bold" />
    </div>

    <v-card class="box-shadow-light pa-4 mx-2 mt-3" flat>
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(saveBacklogStock)">
          <div class="d-flex flex-row flex-grow-1">
            <ValidationProvider
              rules="required"
              class="d-flex flex-grow-1 mr-3"
              v-slot="{ errors }"
            >
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    prepend-inner-icon="event"
                    label="Backlog Stock Date"
                    hint="Use a date in the month the backlog fuel sales belong to"
                    persistent-hint
                    :error-messages="errors[0]"
                    class="font font-weight-medium"
                  />
                </template>
                <v-date-picker
                  v-model="date"
                  :max="today"
                  @input="menu = false"
                />
              </v-menu>
            </ValidationProvider>

            <v-btn
              color="primary"
              class="mt-2"
              :loading="loading"
              :disabled="loading || stockLineItems.length === 0 || !branchId"
              type="submit"
            >
              <span class="font font-weight-medium font-size-sm">Save</span>
            </v-btn>
          </div>

          <v-alert dense outlined type="info" class="font font-weight-medium">
            Backlog stock is isolated from current stock and is only used for
            backlog fuel sales recording.
          </v-alert>
          <v-alert
            v-if="prefillLoading"
            dense
            outlined
            type="info"
            class="font font-weight-medium"
          >
            Checking for existing backlog stock records...
          </v-alert>
          <v-alert
            v-else-if="hasExistingBacklogStock"
            dense
            outlined
            type="warning"
            class="font font-weight-medium"
          >
            Existing backlog stock records were loaded for this month. Saving
            will update those historical values.
          </v-alert>

          <SkeletonPreloader
            v-if="listLoading"
            class="d-flex flex-column my-1"
            :count="5"
            type="list-item-two-line"
          />

          <template v-else>
            <v-card
              v-for="row in stockLineItems"
              :key="row.productId"
              class="elevation-0 d-flex flex-column flex-fill mt-2 pt-4 mb-2 px-2 grey lighten-4"
            >
              <div class="d-flex flex-row flex-grow-1">
                <v-text-field
                  v-model="row.name"
                  readonly
                  outlined
                  prepend-inner-icon="local_gas_station"
                  label="Product"
                  class="font font-weight-medium mx-2"
                />
                <ValidationProvider
                  rules="required|decimals"
                  class="d-flex flex-grow-1 mx-2"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="row.openingStock"
                    outlined
                    prepend-inner-icon="inventory"
                    label="Historical Stock Level"
                    suffix="Lts"
                    :error-messages="errors[0]"
                    class="font font-weight-medium"
                  />
                </ValidationProvider>
                <ValidationProvider
                  rules="decimals"
                  class="d-flex flex-grow-1 mx-2"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="row.meterReading"
                    outlined
                    prepend-inner-icon="speed"
                    label="Reference Meter Reading"
                    :error-messages="errors[0]"
                    class="font font-weight-medium"
                  />
                </ValidationProvider>
              </div>
            </v-card>
          </template>
        </form>
      </ValidationObserver>
    </v-card>

    <SnackBar
      :showSnackBar="showSnackBar"
      :status="status"
      :message="message"
    />
  </v-container>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { Api } from "../../api/Api";
import PageTitle from "../../components/PageTitle";
import { decimals } from "../../utils/resolvers";

extend("required", {
  ...required,
  message: "Field is required"
});
extend("decimals", decimals);

export default {
  name: "AddBacklogFuelStock",
  components: {
    PageTitle,
    ValidationObserver,
    ValidationProvider,
    SnackBar: () => import(`../../components/SnackBar`),
    SkeletonPreloader: () => import(`../../components/SkeletonPreloader`)
  },
  data() {
    return {
      menu: false,
      today: moment().format("YYYY-MM-DD"),
      date: moment()
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
      existingBacklogStocks: [],
      prefillLoading: false,
      prefillRequestKey: "",
      stockLineItems: []
    };
  },
  computed: {
    ...mapGetters({
      products: "products/getProductList",
      branch: "settings/getBranchDetails",
      profile: "auth/getProfileDetails",
      loading: "getIsLoading",
      listLoading: "getIsListLoading",
      showSnackBar: "getSnackBarState",
      message: "getSnackBarMessage",
      status: "getSnackBarStatus"
    }),
    branchId() {
      return (
        this.profile?.branchId?.id || this.profile?.branchId || this.branch?.id
      );
    },
    hasExistingBacklogStock() {
      return this.existingBacklogStocks.length > 0;
    }
  },
  watch: {
    branchId() {
      this.fetchExistingBacklogStock();
    },
    date() {
      this.fetchExistingBacklogStock();
    },
    products: {
      immediate: true,
      handler(products) {
        this.buildStockLineItems(products, this.existingBacklogStocks);
        this.fetchExistingBacklogStock();
      }
    }
  },
  methods: {
    getStockProductId(stock) {
      return stock?.productId?.id || stock?.productId;
    },
    buildStockLineItems(products = this.products, existingStocks = []) {
      const existingStocksByProduct = existingStocks.reduce((carry, stock) => {
        carry[this.getStockProductId(stock)] = stock;
        return carry;
      }, {});

      this.stockLineItems = products
        .filter(product => !product.isLube)
        .map(product => {
          const existingStock = existingStocksByProduct[product.id];
          return {
            productId: product.id,
            name: product.name,
            openingStock: existingStock
              ? Number(existingStock.quantity ?? 0)
              : 0,
            minimumThreshold: existingStock
              ? Number(existingStock.minimumThreshold ?? 0)
              : product.minimumThreshold ?? 0,
            meterReading: existingStock
              ? Number(existingStock.meterReading ?? 0)
              : 0
          };
        });
    },
    async fetchExistingBacklogStock() {
      if (!this.branchId || !this.date) {
        this.existingBacklogStocks = [];
        this.buildStockLineItems(this.products, []);
        return;
      }

      const requestKey = `${this.branchId}-${this.date}`;
      this.prefillRequestKey = requestKey;
      this.prefillLoading = true;

      try {
        const response = await Api().get(
          `/stocks/backlog/openings/${this.branchId}/products?date=${this.date}`
        );

        if (this.prefillRequestKey !== requestKey) {
          return;
        }

        this.existingBacklogStocks = Array.isArray(response.data)
          ? response.data
          : [];
        this.buildStockLineItems(this.products, this.existingBacklogStocks);
      } catch (e) {
        const errorMessage = e?.response?.data?.message;
        this.$store.dispatch("showSnackBar", {
          snackBar: true,
          message: Array.isArray(errorMessage)
            ? errorMessage.join(", ")
            : errorMessage ?? "Unable to fetch existing backlog fuel stock",
          status: "red"
        });
      } finally {
        if (this.prefillRequestKey === requestKey) {
          this.prefillLoading = false;
        }
      }
    },
    async saveBacklogStock() {
      try {
        this.$store.dispatch("isLoading", true);
        await Api().post(`/stocks/backlog/openings/${this.branchId}/products`, {
          date: this.date,
          stockLineItems: this.stockLineItems.map(
            ({ productId, openingStock, minimumThreshold, meterReading }) => ({
              productId,
              openingStock: Number(openingStock),
              minimumThreshold: Number(minimumThreshold ?? 0),
              meterReading: Number(meterReading ?? 0)
            })
          )
        });
        this.$store.dispatch("isLoading", false);
        this.$store.dispatch("showSnackBar", {
          snackBar: true,
          message: "Backlog fuel stock saved successfully",
          status: "success"
        });
        await this.fetchExistingBacklogStock();
      } catch (e) {
        const errorMessage = e?.response?.data?.message;
        this.$store.dispatch("isLoading", false);
        this.$store.dispatch("showSnackBar", {
          snackBar: true,
          message: Array.isArray(errorMessage)
            ? errorMessage.join(", ")
            : errorMessage ?? "Unable to save backlog fuel stock",
          status: "red"
        });
      }
    }
  },
  created() {
    this.$store.dispatch("products/list", {
      isPaginated: false,
      page: 1,
      itemsPerPage: 100
    });
    this.$store.dispatch("auth/me");
    this.$store.dispatch("settings/branch");
  }
};
</script>
